/**
 * ReportingSectionsFindings — the "Findings" and "CAPs & validation" sections
 * of the Reporting tab: severity × status matrix with classification chips,
 * aging bars, the repeat-findings table with its red badge, CAP aging and
 * status distributions, and the CAR validation-readiness list with the
 * amber/red validation-window flags.
 */
import React from 'react';
import { Section, MicroLabel, useAnalytics, fmtPct, fmtDate } from './ReportingShared.jsx';
import { BucketBars, MeterBar } from './ReportingCharts.jsx';
import {
  SEVERITY_LABELS, FINDING_STATUS_LABELS, CLASSIFICATION_LABELS,
  FINDING_AGE_LABELS, CAP_AGING_LABELS, CAP_STATUS_LABELS,
} from './labels.js';

const SEV_ORDER = ['critical', 'high', 'medium', 'low', 'unspecified'];
const STATUS_ORDER = ['open', 'cap_required', 'cap_in_progress', 'remediated', 'closed'];
const SEV_TEXT = { critical: 'text-red-700', high: 'text-orange-700', medium: 'text-amber-700', low: 'text-slate-600', unspecified: 'text-slate-400' };
const AGE_COLORS = { '0-30': '#cbd5e1', '31-60': '#fbbf24', '61-90': '#fb923c', '90+': '#ef4444' };
const CAP_AGE_ORDER = ['no_due_date', 'not_yet_due', 'overdue_1_30', 'overdue_31_60', 'overdue_61_90', 'overdue_90_plus'];
const CAP_AGE_COLORS = { no_due_date: '#cbd5e1', not_yet_due: '#10b981', overdue_1_30: '#fbbf24', overdue_31_60: '#fb923c', overdue_61_90: '#f97316', overdue_90_plus: '#ef4444' };

export function FindingsSection({ qs }) {
  const { loading, error, data } = useAnalytics('findings', qs);
  const f = data?.findings;
  const matrix = f?.matrix || [];
  const repeats = f?.repeat_findings || [];
  const aging = f?.open_aging || [];
  const sevs = SEV_ORDER.filter(s => matrix.some(m => m.severity === s));
  const statuses = STATUS_ORDER.filter(s => matrix.some(m => m.status === s));
  const cell = (sev, st) => matrix.find(m => m.severity === sev && m.status === st)?.n || 0;
  return (
    <Section
      title="Findings"
      explainer="Every finding by how severe it is and where it stands, how long the open ones have sat, and the conditions that keep coming back."
      loading={loading} error={error} empty={!matrix.length && !repeats.length}
      emptyText="No findings in this period. When fieldwork raises deficiencies they are counted here."
    >
      <div className="flex flex-wrap gap-1.5 mb-3">
        {(f?.classification || []).map(c => (
          <span key={c.classification} className="text-[11px] font-semibold text-slate-600 bg-slate-100 rounded-full px-2.5 py-0.5">
            {CLASSIFICATION_LABELS[c.classification] || 'Unclassified'} <span className="text-slate-400">{c.n}</span>
          </span>
        ))}
      </div>
      {matrix.length > 0 && (
        <div className="overflow-x-auto">
          <MicroLabel>Severity by status</MicroLabel>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                <th className="py-1 pr-3">Severity</th>
                {statuses.map(s => <th key={s} className="py-1 pr-3 text-right">{FINDING_STATUS_LABELS[s] || s}</th>)}
                <th className="py-1 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {sevs.map(sev => (
                <tr key={sev} className="border-t border-slate-100">
                  <td className={`py-1.5 pr-3 font-semibold ${SEV_TEXT[sev]}`}>{SEVERITY_LABELS[sev] || sev}</td>
                  {statuses.map(st => <td key={st} className="py-1.5 pr-3 text-right tabular-nums text-slate-700">{cell(sev, st) || <span className="text-slate-300">0</span>}</td>)}
                  <td className="py-1.5 text-right font-bold tabular-nums text-slate-800">{statuses.reduce((t, st) => t + cell(sev, st), 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {aging.length > 0 && (
        <div className="mt-4">
          <MicroLabel>Open findings by age</MicroLabel>
          <BucketBars items={aging.map(b => ({ label: FINDING_AGE_LABELS[b.bucket] || b.bucket, value: b.n, color: AGE_COLORS[b.bucket] || '#cbd5e1' }))} />
        </div>
      )}
      {repeats.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <MicroLabel>Repeat findings, the same condition raised in two or more audits</MicroLabel>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                <th className="py-1 pr-3">Condition</th>
                <th className="py-1 pr-3 text-right">Audits</th>
                <th className="py-1 pr-3">Subjects</th>
                <th className="py-1">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {repeats.map(r => (
                <tr key={r.repeat_key} className="border-t border-slate-100 text-slate-700">
                  <td className="py-1.5 pr-3">
                    <span className="text-[10px] font-bold uppercase text-red-700 bg-red-100 rounded px-1.5 py-0.5 mr-2">Repeat finding</span>
                    <span className="font-mono text-slate-600">{r.citation || r.criterion_key}</span>
                  </td>
                  <td className="py-1.5 pr-3 text-right font-bold tabular-nums">{r.audit_count}</td>
                  <td className="py-1.5 pr-3 text-slate-500">{(r.subjects || []).join(', ')}</td>
                  <td className="py-1.5 text-slate-500">{fmtDate(r.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

export function CapsSection({ qs }) {
  const { loading, error, data } = useAnalytics('caps', qs);
  const c = data?.caps;
  const aging = c?.cap_aging || [];
  const statusDist = (c?.cap_status || []).filter(s => Number(s.n) > 0);
  const v = c?.validation_readiness;
  const unvalidated = c?.audits_with_unvalidated_cars || [];
  const orderedAging = CAP_AGE_ORDER.map(b => aging.find(a => a.bucket === b)).filter(Boolean);
  const empty = !aging.length && !statusDist.length && !unvalidated.length;
  return (
    <Section
      title="CAPs & validation"
      explainer="Corrective action plans: how old the open ones are, where each plan stands, and which CARs still need validation before close out."
      loading={loading} error={error} empty={empty}
      emptyText="No corrective action plans in this period. CAPs appear once findings require corrective action."
    >
      {orderedAging.length > 0 && (
        <div>
          <MicroLabel>Open CAPs by due date</MicroLabel>
          <BucketBars items={orderedAging.map(b => ({ label: CAP_AGING_LABELS[b.bucket] || b.bucket, value: b.n, color: CAP_AGE_COLORS[b.bucket] || '#cbd5e1' }))} />
        </div>
      )}
      {statusDist.length > 0 && (
        <div className="mt-3">
          <MicroLabel>Plan status</MicroLabel>
          <div className="flex flex-wrap gap-1.5">
            {statusDist.map(s => (
              <span key={s.cap_status} className="text-[11px] font-semibold text-slate-600 bg-slate-100 rounded-full px-2.5 py-0.5">
                {CAP_STATUS_LABELS[s.cap_status] || s.cap_status} <span className="text-slate-400">{s.n}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      {v && (v.totals.validated + v.totals.not_validated) > 0 && (
        <div className="mt-4 max-w-sm">
          <MeterBar label="CARs validated" rate={v.validated_rate} barClass="bg-emerald-500"
            sub={`${v.totals.validated} validated, ${v.totals.not_validated} still waiting (${fmtPct(v.validated_rate)} done)`} />
        </div>
      )}
      {unvalidated.length > 0 && (
        <div className="mt-4">
          <MicroLabel>Validation readiness, audits still carrying unvalidated CARs</MicroLabel>
          <div className="space-y-1.5">
            {unvalidated.map(a => {
              const d = a.days_since_final;
              const tone = d === null || d === undefined ? 'text-slate-500 bg-slate-50 border-slate-200'
                : d > 150 ? 'text-red-700 bg-red-50 border-red-200'
                : d > 90 ? 'text-amber-700 bg-amber-50 border-amber-200'
                : 'text-slate-600 bg-slate-50 border-slate-200';
              return (
                <div key={a.audit_id} className="flex items-center gap-2 flex-wrap border border-slate-100 rounded-lg px-3 py-2">
                  <span className="text-xs font-semibold text-slate-800">{a.subject_name}</span>
                  <span className="text-[11px] text-slate-500">{a.unvalidated_cars} unvalidated CAR{a.unvalidated_cars === 1 ? '' : 's'}</span>
                  <span className={`ml-auto text-[11px] font-semibold border rounded-full px-2 py-0.5 ${tone}`}>
                    {d === null || d === undefined ? 'No final report yet' : `${d}d since the final report`}
                  </span>
                  {d !== null && d !== undefined && d > 150 && <span className="text-[10px] text-red-600 w-full">Well past the usual validation window, validate or escalate.</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Section>
  );
}
